import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link } from "react-router-dom";
import { useLazyVerifyEmailQuery, useSendVerifyCodeMutation } from "store/api";
import PinputCode from '../../components/PinputCode/PinputCode';

export interface PageSignUpProps {
  className?: string;
}

type AlertType = "warning" | "error" | "success" | null;


const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const [validateEmail] = useLazyVerifyEmailQuery();
  const [sendCode] = useSendVerifyCodeMutation();
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<AlertType>("error");
  const [showCode, setShowCode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [isRunning, setIsRunning] = useState(false);
  const [formJson, setFormJson] = useState<any>({});

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return; 

    const intervalId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(intervalId); 
  }, [isRunning, timeLeft]);

  const startTimer = () => {
    setIsRunning(true); 
  };

  const resetTimer = () => {
    setIsRunning(false); 
    setTimeLeft(120);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlertType('error');
    const form = event.currentTarget;
    let isValid = true;
    const fields = form.elements;
    
  
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i] as HTMLInputElement;
      if (field.type !== "submit" && !field.value.trim()) {
        isValid = false;
        break;
      }
    }
  
    if (!isValid) {
      setAlert('Por favor, completa todos los campos');
      setTimeout(() => setAlert(null), 2500);
      return;
    }

    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const repeatPassword = (form.elements.namedItem('repeatPassword') as HTMLInputElement).value;

    if (password.length < 4) {
      setAlert('La contraseña debe tener al menos 4 caracteres');
      setTimeout(() => setAlert(null), 2500);
      return;
    }
    if (password !== repeatPassword) {
      setAlert('Las contraseñas no coinciden');
      setTimeout(() => setAlert(null), 2500);
      return;
    }
  
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const { data, error } = await validateEmail(email);
    if (error){
      if ('status' in error) {
        const fetchError = error as { status: number; data: { message: string; statusMessage: string } };
        switch (fetchError.status) {
          case 409:
            const { data:dataUserDeleted } = await sendCode({ email });
            if (dataUserDeleted) {
              if (timeLeft>0)startTimer();
              else {
                resetTimer();
                startTimer();
              }
              setAlertType('success');
              setShowCode(true);
              const formData = new FormData(form);
              const jsonData = Object.fromEntries(formData.entries());
              setFormJson(jsonData);
              setAlert('Se ha enviado un código de verificación a su correo, por favor verifique su bandeja de entrada o spam');
            }
            break;
          case 410:
            const { data } = await sendCode({ email });
            if (data) {
              if (timeLeft>0)startTimer();
              else {
                resetTimer();
                startTimer();
              }
              setAlertType('success');
              setShowCode(true);
              const formData = new FormData(form);
              const jsonData = Object.fromEntries(formData.entries());
              setFormJson(jsonData);
              setAlert('Se ha enviado un código de verificación a su correo, por favor verifique su bandeja de entrada o spam');
            }
            break;
          default:
            setAlert('Algo salió mal. Intenta nuevamente.');
        }
        setTimeout(() => setAlert(null), 3500);
      }
    }
    if (data) {
      if (data.meta.status === 200) {
        setAlert('Este correo ya está registrado');
        setTimeout(() => setAlert(null), 2500);
      }
    }
  };
  

  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <Helmet>
        <title>Bee || Registro</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Registro
        </h2>
        <div className="max-w-md mx-auto space-y-6 ">
          <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit}>
            <label className="block">
              <Input
                name="name"
                placeholder="Nombre"
                className="mt-1"
              />
            </label>
            <label className="block">
              <Input
                name="email"
                type="email"
                placeholder="example@example.com"
                className="mt-1"
                onChange={(e) => { setShowCode(false); resetTimer(); }}
              />
            </label>
            <label className="block">
              <Input name="password" type="password" className="mt-1" placeholder="Contraseña"/>
            </label>
            <label className="block">
              <Input name="repeatPassword" type="password" className="mt-1" placeholder="Repetir Contraseña"/>
            </label>
            {showCode &&timeLeft>0 && <p>El código caducará en {formatTime(timeLeft)}</p>}
            {showCode &&timeLeft>0 && <PinputCode form={formJson}></PinputCode>}
            <ButtonPrimary disabled={showCode&&timeLeft>0} type="submit">{showCode? 'Enviar otro código':'Continuar'}</ButtonPrimary>
          </form>

          {/* ==== */}
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Ya tienes una cuenta? {` `}
            <Link to="/login">Ingresar</Link>
          </span>
        </div>
      </div>
        {alert && (
        <div
          className={`fixed bottom-8 left-1/2 transform -translate-x-1/2  ${alertType==='warning'?'bg-yellow-600':alertType==='error'?'bg-red-600':'bg-green-600'} text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out z-50`}
          style={{ opacity: 1 }}
        >
          {alert}
        </div>
      )}

    </div>
  );
};

export default PageSignUp;
